import {Inject, Injectable, NgZone} from '@angular/core';
import {Profile, User, UserManager, WebStorageStateStore} from 'oidc-client';
import {BehaviorSubject, from, Observable, of, Subject} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {AuthConf} from './auth_conf';
import {Events} from './constants';

/** Passed to /login as `?reason=` so the page can say why the user has to sign in again. */
export type SignInReason = 'signed-out' | 'unauthorized' | 'callback-failed';

@Injectable({
  providedIn: 'root'
})
export class ErisLoginService {
  userManager: UserManager;
  private dataSource = new BehaviorSubject<User | null>(null);
  /** localStorage key oidc-client persists the user under; other tabs observe it through 'storage' events. */
  private readonly userStoreKey: string;
  private handlingUnauthorized = false;

  constructor(@Inject('authConf') authConf: AuthConf, private zone: NgZone) {
    const settings = {
      authority: authConf.authority,
      client_id: authConf.client_id,
      redirect_uri: authConf.redirect_uri,
      post_logout_redirect_uri: authConf.post_logout_redirect_uri,
      response_type: authConf.response_type,
      scope: authConf.scope,
      userStore: new WebStorageStateStore({store: window.localStorage}),
      // Identity sets X-Frame-Options: SAMEORIGIN / frame-ancestors 'none', so
      // oidc-client must not open checksession/silent-renew iframes against it.
      monitorSession: false,
      automaticSilentRenew: false
    };
    this.userManager = new UserManager(settings);
    this.userStoreKey = `oidc.user:${settings.authority}:${settings.client_id}`;

    this.userManager.events.addUserLoaded(user => {
      if (user && user.access_token) {
        this.ensureUser(user);
      }
    });

    // oidc-client removed the persisted user (sign-out, callbacks, revocation).
    this.userManager.events.addUserUnloaded(() => this.clearSession('signed-out'));

    this.userManager.events.addUserSignedOut(async () => {
      await this.userManager.removeUser();
      this.clearSession('signed-out');
      this._eventSource.next(Events.USER_SIGNED_OUT);
    });

    this.userManager.events.addUserSignedIn(async () => {
      this._eventSource.next(Events.USER_SIGNED_IN);
    });

    this.userManager.events.addAccessTokenExpired(async () => {
      // Silent renew is off, so an expired token means a full sign-in through the STS. The shell
      // handles TOKEN_EXPIRED by going to /login, which starts that sign-in automatically; the
      // in-memory user is dropped first so removeUser() does not also raise `sessionCleared`
      // (that would show the "Sign in" button instead of re-signing in).
      this._eventSource.next(Events.TOKEN_EXPIRED);
      this.dataSource.next(null);
      await this.userManager.removeUser();
    });

    this.listenForBrowserEvents();
  }

  private _eventSource: BehaviorSubject<Events> = new BehaviorSubject<Events>(Events.EMPTY);

  /** Emits whenever a signed-in session is dropped locally (sign-out here or in another tab, token rejected). */
  private _sessionCleared = new Subject<SignInReason>();

  get eventSource(): Observable<Events> {
    return this._eventSource.asObservable();
  }

  get sessionCleared(): Observable<SignInReason> {
    return this._sessionCleared.asObservable();
  }

  /**
   * Resolves the current user once (memory first, then storage) and completes. It deliberately does not
   * keep emitting: a long-lived subscription from the login page would otherwise start a sign-in redirect
   * the moment the session is cleared, racing the sign-out redirect.
   */
  public getUser(): Observable<User | null> {
    const cached = this.dataSource.value;
    if (cached) {
      return of(cached);
    }

    return from(this.userManager.getUser()).pipe(
      tap(user => {
        if (user) {
          this.dataSource.next(user);
        }
      }),
      map(user => user || null)
    );
  }

  public instantiate(): void {
    this.getUser().subscribe();
  }

  getToken(): string {
    return this.dataSource.value ? this.dataSource.value.access_token : null;
  }

  /**
   * Facility-registry account.user id from OIDC profile / access token.
   * Prefer profile.userId (eris scope); fall back to PrimarySid / JWT payload.
   */
  getUserId(): number {
    const user = this.dataSource.value;
    const fromProfile = this.parseUserId(user?.profile);
    if (fromProfile > 0) {
      return fromProfile;
    }
    return this.parseUserIdFromAccessToken(user?.access_token);
  }

  getProfile(): Profile {
    return this.dataSource.value ? this.dataSource.value.profile : null;
  }

  private parseUserId(profile: Profile | Record<string, any> | null | undefined): number {
    if (!profile) {
      return 0;
    }
    const candidates = [
      profile['userId'],
      profile['userid'],
      profile['primarysid'],
      profile['http://schemas.microsoft.com/ws/2008/06/identity/claims/primarysid']
    ];
    for (const value of candidates) {
      const id = Number(value);
      if (Number.isFinite(id) && id > 0) {
        return id;
      }
    }
    return 0;
  }

  private parseUserIdFromAccessToken(accessToken: string | null | undefined): number {
    if (!accessToken) {
      return 0;
    }
    try {
      const payload = accessToken.split('.')[1];
      if (!payload) {
        return 0;
      }
      const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return this.parseUserId(json);
    } catch {
      return 0;
    }
  }

  isTokenExpired(): boolean {
    return this.dataSource.value ? this.dataSource.value.expired : true;
  }

  /**
   * Guard-facing check. Reads the persisted user rather than the in-memory copy: memory survives a
   * sign-out when the page is restored from the back/forward cache, storage does not.
   */
  isAuthenticated(): Observable<boolean> {
    return from(this.userManager.getUser()).pipe(
      map(user => {
        if (user && !user.expired) {
          this.ensureUser(user);
          return true;
        }
        this.dataSource.next(null);
        return false;
      })
    );
  }

  getUserObservable(): Observable<User | null> {
    return this.dataSource.asObservable();
  }

  cleanUp(): void {
    this.userManager.events.removeUserSignedIn(() => {
    });
    this.userManager.events.removeUserSignedOut(() => {
    });
    this.userManager.events.removeAccessTokenExpired(() => {
    });
  }

  completeAuthentication(): Promise<string> {
    return this.userManager.signinRedirectCallback().then(user => {
      this.ensureUser(user);
      return user.access_token;
    });
  }

  /** Ensure in-memory user is set (e.g. interceptor loaded from storage before instantiate finished). */
  ensureUser(user: User): void {
    if (user && (!this.dataSource.value || this.dataSource.value.access_token !== user.access_token)) {
      this.dataSource.next(user);
    }
  }

  /** Finishes a sign-out that the STS redirected back to `auth-callback?to=signout`. */
  completeSignout(): Promise<void> {
    return this.userManager.signoutRedirectCallback()
      .catch(err => console.warn('Sign-out callback could not be processed', err))
      .then(() => this.userManager.removeUser())
      .then(() => this.clearSession('signed-out'));
  }

  public login(): Promise<void> {
    return this.userManager.signinRedirect();
  }

  public renewToken(): void {
    // Silent renew uses an iframe; id.eris.efda.gov.et refuses framing.
    // Fall back to a full redirect login when the access token expires.
    this.login().catch(err => console.log(err));
  }

  /**
   * Signs out everywhere we can reach:
   *  1. drop the in-memory session (listeners wipe cached account data and show the sign-in page),
   *  2. best-effort revoke refresh/reference tokens at the STS (JWT access tokens are skipped by oidc-client),
   *  3. remove the persisted user (other tabs react through the 'storage' event) and redirect to the STS end-session endpoint.
   */
  public async logout(): Promise<void> {
    this.clearSession('signed-out');
    try {
      await this.userManager.revokeAccessToken();
    } catch (err) {
      console.warn('Token revocation failed; continuing sign-out', err);
    }
    return this.userManager.signoutRedirect();
  }

  /** The API rejected our token (401): drop the persisted user and ask the user to sign in again. */
  handleUnauthorized(): void {
    if (this.handlingUnauthorized) {
      return;
    }
    this.handlingUnauthorized = true;
    this.clearSession('unauthorized');
    this.userManager.removeUser()
      .catch(() => undefined)
      .then(() => {
        this.handlingUnauthorized = false;
      });
  }

  /** Forgets the signed-in user. Emits `sessionCleared` (once) if there was one. */
  clearSession(reason: SignInReason = 'signed-out'): void {
    if (this.dataSource.value === null) {
      return;
    }
    this.dataSource.next(null);
    this._sessionCleared.next(reason);
  }

  /**
   * Two situations where JavaScript state outlives the sign-in:
   *  - the page is restored from the back/forward cache after signing out (Back button),
   *  - another tab signs out (oidc-client removes the persisted user, which fires 'storage' here).
   * In both cases re-read storage and, if the user is gone, drop the session.
   */
  private listenForBrowserEvents(): void {
    this.zone.runOutsideAngular(() => {
      window.addEventListener('pageshow', (event: PageTransitionEvent) => {
        if (event.persisted) {
          this.zone.run(() => this.verifyStoredUser());
        }
      });
      window.addEventListener('storage', (event: StorageEvent) => {
        if (event.key === this.userStoreKey) {
          this.zone.run(() => this.verifyStoredUser());
        }
      });
    });
  }

  private verifyStoredUser(): void {
    this.userManager.getUser().then(user => {
      if (user && !user.expired) {
        this.ensureUser(user);
        return;
      }
      this.clearSession('signed-out');
    });
  }
}
